import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { Search } from 'lucide-react'
import { cx } from './cx.ts'

const controlBase =
  'w-full rounded-lg border border-line bg-white px-3.5 text-[14.5px] text-text placeholder:text-muted/70 ' +
  'focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 disabled:bg-mist disabled:text-muted ' +
  'aria-invalid:border-live aria-invalid:focus:ring-live/20'

export interface FieldProps {
  label: ReactNode
  hint?: ReactNode
  error?: string | null
  required?: boolean
  children: (id: string, describedBy: string | undefined) => ReactNode
  className?: string
}

/** Label + control + hint/error, wired for screen readers. */
export function Field({ label, hint, error, required, children, className }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errId = error ? `${id}-err` : undefined
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="label-caps">
        {label}
        {required && <span className="text-live" aria-hidden> *</span>}
      </label>
      {children(id, describedBy)}
      {hint && !error && (
        <p id={hintId} className="text-[12.5px] leading-snug text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} role="alert" className="text-[12.5px] font-semibold text-live">
          {error}
        </p>
      )}
    </div>
  )
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx(controlBase, 'min-h-11', className)} {...rest} />
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, rows = 4, ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={cx(controlBase, 'py-2.5 leading-relaxed', className)} {...rest} />
})

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { options: SelectOption[]; placeholder?: string }>(
  function Select({ className, options, placeholder, ...rest }, ref) {
    return (
      <select ref={ref} className={cx(controlBase, 'min-h-11 appearance-auto pr-8', className)} {...rest}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    )
  },
)

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  className,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: ReactNode
  description?: ReactNode
  disabled?: boolean
  className?: string
}) {
  const id = useId()
  return (
    <div className={cx('flex items-start gap-3', className)}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-5 shrink-0 accent-forest"
      />
      <label htmlFor={id} className="flex flex-col gap-0.5 text-[14px] leading-snug">
        <span className="font-semibold">{label}</span>
        {description && <span className="text-[12.5px] text-muted">{description}</span>}
      </label>
    </div>
  )
}

/** On/off switch. */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  size = 'md',
  describedBy,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  disabled?: boolean
  size?: 'sm' | 'md'
  describedBy?: string
}) {
  const w = size === 'sm' ? 'w-10 h-6' : 'w-12 h-7'
  const k = size === 'sm' ? 'size-[18px]' : 'size-[22px]'
  const on = size === 'sm' ? 'translate-x-[19px]' : 'translate-x-[23px]'
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'relative inline-flex shrink-0 items-center rounded-full p-[3px] transition disabled:opacity-50',
        'min-h-0 before:absolute before:-inset-2.5 before:content-[""]',
        w,
        checked ? 'bg-forest' : 'bg-[#cfd6d1]',
      )}
    >
      <span className={cx('rounded-full bg-white shadow transition-transform', k, checked ? on : 'translate-x-0')} />
    </button>
  )
}

/** Pill-shaped toggle chip used for audience and filter picks. */
export function Chip({
  selected,
  onClick,
  children,
  disabled,
  tone = 'light',
}: {
  selected: boolean
  onClick: () => void
  children: ReactNode
  disabled?: boolean
  tone?: 'light' | 'dark'
}) {
  const light = selected ? 'bg-forest text-white border-forest' : 'bg-white text-text border-line hover:border-forest/50'
  const dark = selected ? 'bg-gold-bright text-ink border-gold-bright' : 'bg-navsurf text-cream border-transparent hover:border-cream/40'
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3.5 text-[12.5px] font-bold tracking-wide transition disabled:opacity-40 lg:min-h-9',
        tone === 'dark' ? dark : light,
      )}
    >
      {children}
    </button>
  )
}

/** Segmented control (day tabs, view switches). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T
  options: { value: T; label: ReactNode }[]
  onChange: (v: T) => void
  label: string
  className?: string
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cx('inline-flex rounded-full bg-mist p-1', className)}>
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={cx(
              'min-h-11 flex-1 rounded-full px-4 text-[13px] font-bold whitespace-nowrap transition lg:min-h-9',
              active ? 'bg-white text-forest shadow-sm' : 'text-muted hover:text-text',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function SearchInput({ value, onChange, placeholder = 'Search', className, label = 'Search' }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string; label?: string }) {
  return (
    <div className={cx('relative', className)}>
      <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" aria-hidden />
      <input
        type="search"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cx(controlBase, 'min-h-11 pl-9')}
      />
    </div>
  )
}
